import { z } from 'zod';
import { waveHoursValid } from './calendar.ts';
import { BRANCH_IDS, EARTH_IDS, MOUNTS, BUILDABLE_TYPES, LAND_ONLY_TYPES, CARRY_MODES, DISTRICT_KINDS, ELIXIR_KINDS, ENCHANTS, FORKS, GEAR_ATTRS, GLYPH_IDS, GEMS, HERO_CLASSES, LOADOUTS, OFFERING_KINDS, PALADIN_AURAS, ALL_RELICS, ROSTER, SKILLS } from '@somnhold/content';

/** Validators for everything a client can send (SPEC §17: all input goes through these). */

const int = z.number().int();
const coord = int.min(-64).max(64);
const amount = int.min(0).max(1_000_000);
const GEAR_ATTR = z.enum(GEAR_ATTRS as [string, ...string[]]);
const res = z
  .object({ wood: amount.optional(), stone: amount.optional(), crystal: amount.optional(), dust: amount.optional(), iron: amount.optional() })
  .strict();
const structure = z.enum(['gate', ...BUILDABLE_TYPES, ...LAND_ONLY_TYPES] as [string, ...string[]]);
const aura = z.enum(PALADIN_AURAS as unknown as [string, ...string[]]);
// A hero's name: the simulation folds and cuts it to ROSTER.nameMax characters (an emoji can take several units here).
const heroName = z.string().max(ROSTER.nameMax * 4);
const pick = z.object({ skill: z.enum(Object.keys(SKILLS) as [string, ...string[]]), variant: z.enum(Object.keys(FORKS) as [string, ...string[]]).nullable() }).strict();
const glyph = z.enum(GLYPH_IDS as unknown as [string, ...string[]]).nullable();
const form = z.enum(['a', 'b']);
const loadout = z.object({ name: z.string().max(LOADOUTS.nameMax).optional(), picks: z.tuple([pick, pick, pick]), aura: aura.optional(), glyphs: z.tuple([glyph, glyph, glyph]).optional(), forms: z.object({ weapon: form.optional(), armor: form.optional(), amulet: form.optional() }).strict().optional(), gear: z.object({ weapon: int.optional(), offhand: int.optional(), head: int.optional(), armor: int.optional(), boots: int.optional(), amulet: int.optional(), ring: int.optional(), ring2: int.optional() }).strict().optional() }).strict();
const post = z.union([z.object({ sector: int.min(0).max(4) }).strict(), z.object({ anywhere: z.literal(true) }).strict(), z.null()]);

export const CampaignCommandSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('blueprint'), q: coord, r: coord, s: structure, turn: int.min(0).max(5).optional() }).strict(),
  z.object({ t: z.literal('contribute'), q: coord, r: coord, res }).strict(),
  z.object({ t: z.literal('work'), q: coord, r: coord, minutes: int.min(1).max(60) }).strict(),
  z.object({ t: z.literal('repair'), q: coord, r: coord }).strict(),
  z.object({ t: z.literal('upgrade'), q: coord, r: coord }).strict(),
  z.object({ t: z.literal('material'), q: coord, r: coord }).strict(),
  z.object({ t: z.literal('branch'), q: coord, r: coord, b: z.enum(BRANCH_IDS as [string, ...string[]]) }).strict(),
  z.object({ t: z.literal('earth'), q: coord, r: coord, e: z.enum(EARTH_IDS as [string, ...string[]]) }).strict(),
  z.object({ t: z.literal('mount'), q: coord, r: coord, s: z.enum(MOUNTS as [string, ...string[]]) }).strict(),
  z.object({ t: z.literal('demolish'), q: coord, r: coord, note: z.string().max(400) }).strict(),
  z.object({ t: z.literal('undoDemolish'), note: int.min(0) }).strict(),
  z.object({ t: z.literal('cancel'), q: coord, r: coord }).strict(),
  z.object({ t: z.literal('elixir'), kind: z.enum(ELIXIR_KINDS as [string, ...string[]]) }).strict(),
  z.object({ t: z.literal('greatWork'), side: int.min(0).max(5).optional(), kind: z.enum(DISTRICT_KINDS as [string, ...string[]]).optional() }).strict(),
  z.object({ t: z.literal('enchant'), q: coord, r: coord, element: z.enum(ENCHANTS as [string, ...string[]]) }).strict(),
  z.object({ t: z.literal('fortify'), q: coord, r: coord }).strict(),
  z.object({ t: z.literal('golems'), wood: int.min(0).max(64), stone: int.min(0).max(64), crystal: int.min(0).max(64), iron: int.min(0).max(64).optional() }).strict(),
  z.object({ t: z.literal('ghostPost'), post }).strict(),
  z.object({ t: z.literal('adopt'), sector: int.min(0).max(4) }).strict(),
  z.object({ t: z.literal('task'), q: coord.nullable(), r: coord.nullable(), want: res, text: z.string().max(200) }).strict(),
  z.object({ t: z.literal('fulfill'), task: int.min(0), res }).strict(),
  z.object({ t: z.literal('equip'), item: int.min(0), ring: z.union([z.literal(0), z.literal(1)]).optional() }).strict(),
  z.object({ t: z.literal('forge'), item: int.min(0), attr: z.enum(['damage', 'hp', 'attackSpeed', 'cooldown', 'armor', 'crit', 'resFire', 'resFrost', 'resLightning', 'resAcid', 'resPoison', 'resShadow']).optional() }).strict(),
  z.object({ t: z.literal('salvage'), item: int.min(0) }).strict(),
  z.object({ t: z.literal('temper'), item: int.min(0) }).strict(),
  z.object({ t: z.literal('carve'), item: int.min(0) }).strict(),
  z.object({ t: z.literal('cure'), target: z.string().min(1).max(64) }).strict(),

  z.object({ t: z.literal('attune'), item: int.min(0), to: z.enum(['fire', 'frost', 'lightning', 'acid', 'poison', 'shadow']) }).strict(),
  z.object({ t: z.literal('reforge'), item: int.min(0), attr: GEAR_ATTR, to: GEAR_ATTR.optional() }).strict(),
  z.object({ t: z.literal('awakenUnique'), item: int.min(0), fuel: z.array(int.min(0)).max(8) }).strict(),
  z.object({ t: z.literal('socket'), item: int.min(0) }).strict(),
  z.object({ t: z.literal('masterwork'), item: int.min(0) }).strict(),
  z.object({ t: z.literal('setGem'), item: int.min(0), at: int.min(0).max(2), gem: z.enum(GEMS as [string, ...string[]]), cut: int.min(0).max(4) }).strict(),
  z.object({ t: z.literal('unsetGem'), item: int.min(0), at: int.min(0).max(2) }).strict(),
  z.object({ t: z.literal('cutGem'), gem: z.enum(GEMS as [string, ...string[]]), cut: z.union([z.literal(0), z.literal(1)]) }).strict(),
  z.object({ t: z.literal('fuseGems'), gem: z.enum(GEMS as [string, ...string[]]), cut: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]) }).strict(),
  z.object({ t: z.literal('awaken'), relic: z.enum(ALL_RELICS as [string, ...string[]]) }).strict(),
  z.object({ t: z.literal('offering'), kind: z.enum(OFFERING_KINDS as [string, ...string[]]) }).strict(),
  z.object({ t: z.literal('relicSwap'), relic: z.enum(ALL_RELICS as [string, ...string[]]), drop: z.enum(ALL_RELICS as [string, ...string[]]) }).strict(),
  z.object({ t: z.literal('gift'), item: int.min(0), to: z.string().min(1).max(40) }).strict(),
  z.object({ t: z.literal('skill'), node: z.string().max(40) }).strict(),
  z.object({ t: z.literal('respec') }).strict(),
  z.object({ t: z.literal('aura'), aura }).strict(),
  z.object({ t: z.literal('loadout'), index: int.min(0).max(LOADOUTS.count - 1), loadout }).strict(),
  z.object({ t: z.literal('carry'), mode: z.enum(CARRY_MODES as unknown as [string, ...string[]]), index: int.min(0).max(LOADOUTS.count - 1) }).strict(),
  z.object({ t: z.literal('newHero'), cls: z.enum(HERO_CLASSES as [string, ...string[]]), name: heroName.optional() }).strict(),
  z.object({ t: z.literal('nameHero'), name: heroName }).strict(),
  z.object({ t: z.literal('switchHero'), index: int.min(0).max(ROSTER.max - 2) }).strict(),
  z.object({ t: z.literal('retireHero'), index: int.min(0).max(ROSTER.max - 2) }).strict(),
  z.object({ t: z.literal('sagaChoice'), chapter: int.min(0).max(20).optional(), act: int.min(0).max(20), choice: z.string().min(1).max(32) }).strict(),
  z.object({ t: z.literal('sagaVote'), chapter: int.min(0).max(20), act: int.min(0).max(20), choice: z.string().min(1).max(32) }).strict(),
  z.object({ t: z.literal('sagaRerun'), chapter: int.min(0).max(20) }).strict(),
  z.object({ t: z.literal('sagaRerunNext'), chapter: int.min(0).max(20), act: int.min(0).max(20) }).strict(),
  z.object({ t: z.literal('sideGo'), saga: z.string().min(1).max(24), chapter: int.min(0).max(20), act: int.min(0).max(20) }).strict(),
  z.object({ t: z.literal('sideChoice'), saga: z.string().min(1).max(24), chapter: int.min(0).max(20), act: int.min(0).max(20), choice: z.string().min(1).max(32) }).strict(),
  z.object({ t: z.literal('hireMercenary'), sector: int.min(0).max(4).optional(), cls: z.enum(HERO_CLASSES as [string, ...string[]]) }).strict(),
]);

const fixed = int.min(-100_000).max(100_000);
export const BattleCommandSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('move'), x: fixed, y: fixed }).strict(),
  z.object({ t: z.literal('turret'), x: fixed, y: fixed }).strict(),
  z.object({ t: z.literal('overload'), x: fixed, y: fixed }).strict(),
  z.object({ t: z.literal('carry') }).strict(),
  z.object({ t: z.literal('leave') }).strict(),
  z.object({ t: z.literal('climb') }).strict(),
  z.object({ t: z.literal('trap') }).strict(),
  z.object({ t: z.literal('strike') }).strict(),
  z.object({ t: z.literal('attack'), id: int.min(0) }).strict(),
  z.object({ t: z.literal('stop') }).strict(),
  z
    .object({ t: z.literal('skill'), slot: z.union([z.literal(0), z.literal(1), z.literal(2)]), x: fixed.optional(), y: fixed.optional(), id: int.min(0).optional(), structure: int.min(0).optional(), keep: z.boolean().optional() })
    .strict(),
]);

export const BattleLogSchema = z
  .array(z.object({ tick: int.min(1).max(4000), cmds: z.array(BattleCommandSchema).max(8) }).strict())
  .max(4000);

/** One entry of a solo session: a bastion command at a session-relative time (ms since the session opened). */
export const SessionEntrySchema = z.object({ seq: int.min(0), at: int.min(0), cmd: CampaignCommandSchema }).strict();

export const SessionBattleSchema = z
  .object({
    marea: int.min(0).max(5),
    wave: int.min(0).max(5),
    sector: int.min(0).max(4),
    /** Session-relative time at which the battle started. */
    at: int.min(0),
    log: BattleLogSchema,
  })
  .strict();

export const SessionIncursionSchema = z.object({ id: z.string().max(80), log: BattleLogSchema }).strict();

/** A saga act can run long (the dragon): up to half an hour of ticks. */
export const SAGA_MAX_TICKS = 12 * 60 * 30;
export const SagaLogSchema = z
  .array(z.object({ tick: int.min(1).max(SAGA_MAX_TICKS), cmds: z.array(BattleCommandSchema).max(8) }).strict())
  .max(12000);
export const SessionSagaSchema = z.object({ id: z.string().max(80), log: SagaLogSchema }).strict();

export const CommitSchema = z
  .object({
    entries: z.array(SessionEntrySchema).max(2000),
    battles: z.array(SessionBattleSchema).max(4).default([]),
    incursions: z.array(SessionIncursionSchema).max(4).default([]),
    /** Saga acts played in this session. */
    sagas: z.array(SessionSagaSchema).max(2).default([]),
    /** The client is leaving: close the session after this commit. */
    final: z.boolean().default(false),
    /** Ghost profile learning samples gathered this session (optional). */
    profile: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

const hour = int.min(0).max(23);
export const CreateCampaignSchema = z
  .object({
    name: z.string().trim().min(1).max(40),
    timezone: z.string().min(1).max(64),
    waveHours: z.tuple([hour, hour, hour]).refine(waveHoursValid),
    depth: z.enum(['light', 'deep']).default('light'),
    lang: z.enum(['es', 'en']).default('es'),
    heroClass: z.enum(HERO_CLASSES as [string, ...string[]]),
    displayName: z.string().trim().min(1).max(24),
    /** The bastion from scratch (docs/CONSTRUCCION.md): terraced land and the keep alone; false, the ring. */
    land: z.boolean().optional(),
  })
  .strict();

export const RenameCampaignSchema = z.object({ name: z.string().trim().min(1).max(40) }).strict();

export const AcceptInviteSchema = z
  .object({ heroClass: z.enum(HERO_CLASSES as [string, ...string[]]), displayName: z.string().trim().min(1).max(24) })
  .strict();

export const MagicSchema = z.object({ email: z.string().trim().toLowerCase().email().max(200), lang: z.enum(['es', 'en']).default('es'), next: z.string().max(200).optional() }).strict();
export const ProfileSchema = z.object({ displayName: z.string().trim().min(1).max(24) }).strict();
export const GhostPostSchema = z.object({ post }).strict();

export type CommitBody = z.input<typeof CommitSchema>;
export type SessionEntry = z.infer<typeof SessionEntrySchema>;
export type SessionBattle = z.infer<typeof SessionBattleSchema>;
export type CreateCampaignBody = z.infer<typeof CreateCampaignSchema>;

/** Server replies. */
export interface SessionOpen {
  sessionId: string;
  openedAt: number;
  stateVersion: number;
  stateHash: string;
  state: unknown;
}

export interface CommitReply {
  status: 'accepted' | 'rebased' | 'rejected';
  /** seq numbers of commands the server discarded (rebased). */
  discarded: number[];
  /** Why they were discarded (applyCampaignCommand's reasons, e.g. heroFighting), each once. */
  why?: string[];
  reason?: string;
  stateVersion: number;
  stateHash: string;
  /** Present when the client must reconcile (rebased, or a wave resolved meanwhile). */
  state?: unknown;
}
